/** The supplier exchange — **ADR-0040**, requirements SUP-01, SUP-02 and part of EXP-14.
 *
 * ## One screen, and the reader may be on either side of it
 *
 * A supplier is its own tenant, so this screen serves a **buyer** chasing a figure and a
 * **supplier** answering — and it is the same list of rows, because the row-level policy already
 * admits exactly the exchanges this organisation is a party to. Each row says `i_am`, so nothing
 * here infers a role from whether a field happens to be set.
 *
 * Two screens would have read better and been worse. "My requests" for a supplier and "my
 * requests" for a buyer are the same rows under the same policy; splitting them invites one of the
 * two to grow a clause the other lacks, and the one that grows it is the one somebody is looking at
 * when the other leaks.
 *
 * ## What each side is shown, and why they are not the same
 *
 * - **A buyer** sees what they asked for, the answer if it has come, and — once an Approver — the
 *   decision to copy it into the inventory. That decision needs two facts nothing can infer: which
 *   of their sites it belongs to, and which agreement permits the copy.
 * - **A supplier** sees what has been asked of them and answers it. They see nothing of the
 *   buyer's inventory, and that is not enforced here: it is enforced by the scope on their own
 *   session, which is what SUP-01 is about.
 *
 * ## Three things said rather than left to be inferred
 *
 * - **A figure copied from a supplier is graded C**, and the panel says so before the reader
 *   accepts rather than after. A third-party assertion with no document the buyer holds is not the
 *   same quality of input as a reading somebody at the company took, and learning that afterwards
 *   is the surprise that makes people stop reading grades.
 * - **Reading an answer and filing it are different permissions.** The agreement list prints the
 *   uses it permits, because an agreement that allows a copy and not a disclosure is a real and
 *   reasonable thing for a supplier to grant.
 * - **A revoked agreement does not un-make a copy.** The row says so where it says "revoked", so
 *   nobody goes looking for a figure that has quietly vanished — it has not.
 *
 * ## Purchases sit below the exchange, and nothing in them has a figure
 *
 * `Procurement.tsx` records a finance ledger and asks the questions each purchase line raises
 * (S3-03). It is mounted here because a supplier's answer is the credible number and a spend line
 * is what is left when nobody has one. No spend line can be priced in either market today
 * (ADR-0044, T-27), so every line says what it is missing instead of carrying a figure.
 *
 * ## What is not here
 *
 * **No document on a submission.** Whether EmitIQ is a controller or a processor of
 * supplier-submitted data is counsel's (B-02), so a submission carries a stated basis and no file.
 * The panel says that, because an upload control that was coming soon would be read as an omission.
 *
 * **No invitation.** A supplier needs an account, and the tokenised link ADR-0040 chose as the
 * front door is unbuilt — a bearer credential in an email is its own design. The screen says whose
 * problem that is rather than pretending the flow is complete.
 */

import { Link } from "@tanstack/react-router";
import { useState } from "react";

import { Loaded, ReadFailed, WriteFailed } from "../components/Loaded";
import { PageHeader } from "../components/PageHeader";
import { Panel } from "../components/Panel";
import { StatusPill } from "../components/StatusPill";
import type { StatusTone } from "../components/StatusPill";
import { useMe } from "../api/auth";
import { useFacilities } from "../api/evidence";
import { isPeriodClosed } from "../api/refusal";
import type { Agreement, AllocationBasisName, Exchange } from "../api/suppliers";
import { useSupplierResponsiveness } from "../api/supplierResponsiveness";
import {
  useAcceptAnswer,
  useAgreements,
  useAnswerRequest,
  useExchange,
} from "../api/suppliers";
import { shortDate } from "../lib/format";
import { RequireSession } from "../shell/RequireSession";
import { PostCloseRetry } from "./PeriodLock";
import { ProcurementPanels } from "./Procurement";
import { RecordPanel, RecordTabs } from "./RecordTabs";
import { ScreenFrame } from "./ScreenFrame";
import { SupplierExchangeOverview } from "./SupplierExchangeOverview";
import { RaiseRequest } from "./SupplierRaise";
import { AgreementCard, Answer, ResponsivenessCard } from "./SupplierParts";
import { GrantAgreement } from "./SupplierGrant";
import { copyPermission, shortfallSentence } from "./supplierPermission";
import { BASIS_LABEL, stateLabel } from "./supplierLabels";
import { keepSearch } from "./search";
import styles from "./Screen.module.css";
import own from "./Suppliers.module.css";
import { useScreen } from "./useScreen";

function cls(...parts: Array<string | false | undefined>): string {
  return parts.filter(Boolean).join(" ");
}

/** Mirrored from `api/suppliers.py`. Accepting another company's figure into the inventory is a
 * statement rather than preparation, so it is an Approver's or an Admin's act — the same
 * separation ADR-0008 draws for verifying a document. */
export const MAY_DECIDE: readonly string[] = ["admin", "approver"];

/** Mirrored from `api/procurement.py`. Answering a purchase line and linking a vendor are
 * collection work; recording a ledger is `MAY_DECIDE`'s, because it moves a document to verified. */
export const MAY_COLLECT: readonly string[] = ["admin", "approver", "contributor"];

export function SupplierResponsivenessPanel() {
  const response = useSupplierResponsiveness();
  return <Panel title="Supplier responsiveness" sub="Requests this workspace created, not email delivery" column="1 / 13">
    <details className={own.limits} data-testid="supplier-responsiveness-basis">
      <summary>Basis and limits</summary>
      <p className={styles.note}>Responsiveness describes requests and first recorded answers, not emissions performance, data quality or permission to copy an answer. Accepted answers count; corrections are not new responses.</p>
    </details>
    <Loaded query={response} of="Supplier responsiveness" empty="No requests created for suppliers yet. Requests addressed to this workspace are not counted here.">
      {(rows) => <ul className={own.list} data-testid="supplier-responsiveness">{rows.map((row) => <li key={row.supplier_org_id}>
        <ResponsivenessCard row={row} heading={<h3 className={own.title}>Supplier <Link to="/suppliers/$supplierId" params={{ supplierId: row.supplier_org_id }} search={keepSearch} data-testid="responsiveness-account-link">{row.supplier_org_id}</Link></h3>} />
      </li>)}</ul>}
    </Loaded>
  </Panel>;
}

/** The three views of this screen, in the URL as `tab` so a view is a link. The exchange is first
 * because it is where somebody has something to do; responsiveness is a read of how suppliers have
 * answered; purchases are the spend-based half that nothing here can price. */
const TABS = [
  { key: "exchange", label: "Exchange" },
  { key: "responsiveness", label: "Responsiveness" },
  { key: "purchases", label: "Purchases" },
] as const;
type TabKey = (typeof TABS)[number]["key"];

function isTab(value: string | undefined): value is TabKey {
  return TABS.some((entry) => entry.key === value);
}

/** What the reader may do, as the screen already knows it (`undefined` while `/auth/me` is in
 * flight). A cue is shown only to a role that can act on it: telling a Viewer a request "needs your
 * answer" beside a sentence saying they cannot answer it is a contradiction on one row. */
interface Can {
  mayCollect: boolean | undefined;
  mayDecide: boolean | undefined;
}

/** Whose move it is, in words, or `null` when it is nobody's here. A buyer is waited on for an
 * answer that has come; a supplier for a request nobody has answered yet. From `i_am`, the state
 * and the role - no permission or agreement is inferred. */
export function needsYou(row: Pick<Exchange, "i_am" | "state">, can: Can): string | null {
  if (row.i_am === "supplier" && row.state === "open" && can.mayCollect === true) return "Needs your answer";
  if (row.i_am === "buyer" && row.state === "answered" && can.mayDecide === true) return "Waiting for your decision";
  return null;
}

/** How many rows are waiting on the reader, from the rows already read. */
function waitingOnYou(rows: readonly Exchange[], can: Can): number {
  return rows.filter((row) => needsYou(row, can) !== null).length;
}

/** A state as a pill. The word is `stateLabel`'s, so the gate's state words are unchanged; the tone
 * says whose move it is. A state this screen does not know is neutral and shows its own name. */
export function stateTone(row: Pick<Exchange, "i_am" | "state">, can: Can): StatusTone {
  if (needsYou(row, can) !== null) return "warn";
  switch (row.state) {
    case "open":
    case "answered":
      return "info";
    case "accepted":
      return "ok";
    case "declined":
      return "crit";
    default:
      return "neutral";
  }
}

/** What may be done with an answer: the sharing agreements, and the form to grant one (ADR-0160).
 * Its own component so the Vendors place can open it beside the supplier requests it governs. */
export function PermissionsPanel({
  exchange,
  agreements,
  mayDecide,
}: {
  exchange: readonly Exchange[] | undefined;
  agreements: ReturnType<typeof useAgreements>;
  mayDecide: boolean | undefined;
}) {
  return (
        <Panel title="Permissions" sub="What may be done with an answer">
          {/* **Permitting a customer** (ADR-0160). Only once both reads are in: who has asked
              and what is already granted are what the form is built from, and an unread list
              is not "nobody has asked". */}
          {exchange && agreements.data ? (
            <GrantAgreement rows={exchange} agreements={agreements.data} />
          ) : null}
          <Loaded
            query={agreements}
            of="The sharing agreements"
            empty={
              <>
                No agreement either way. A supplier grants one to say what a customer may do with an
                answer — read it, copy it into an inventory, disclose it in a filing. They are three
                permissions and not one.
              </>
            }
          >
            {(rows) => (
              <ul className={cls(own.agreements)} data-testid="agreements">
                {rows.map((row) => (
                  <AgreementCard key={row.id} row={row} mayDecide={mayDecide} />
                ))}
              </ul>
            )}
          </Loaded>

          <details className={own.limits} data-testid="supplier-basis-limits">
            <summary>Basis and limits</summary>
            <p className={styles.note}>
              <strong>A submission carries no document.</strong> Whether EmitIQ is a controller or a
              processor of data a supplier sends is a question for counsel, and until it is answered a
              supplier states a basis in words and attaches nothing. That is a boundary rather than a
              feature still to come.
            </p>
            <p className={styles.note}>
              <strong>A supplier needs an account.</strong> The single-use invitation link is not available yet,
              so somebody at the supplier has to be set up here first — which is fine for a pilot and
              not for a hundred suppliers. Only a supplier this workspace has already dealt with can be
              asked from this screen.
            </p>
          </details>
        </Panel>
  );
}

function SuppliersScreenBody() {
  const screen = useScreen();
  const { data: me } = useMe();
  const exchange = useExchange();
  const agreements = useAgreements();
  //: `undefined` while `/auth/me` is in flight, and that is **not** "may not decide": a screen that
  //: read it as false would flash the "an Approver accepts" sentence at an Approver.
  const mayDecide = me ? MAY_DECIDE.includes(me.role) : undefined;
  const mayCollect = me ? MAY_COLLECT.includes(me.role) : undefined;
  const tab: TabKey = isTab(screen.tab) ? screen.tab : "exchange";
  // Counted only from a successful read: a failed or pending read is not "nothing waits on you".
  const waiting = exchange.data && me ? waitingOnYou(exchange.data, { mayCollect, mayDecide }) : null;

  return (
    <ScreenFrame screen={screen} title="Suppliers" crumb="what we asked, and what came back" bodyScrolls>
      <PageHeader
        subtitle="What this workspace asked suppliers, what came back, and what may be done with it."
        tabs={
          <RecordTabs
            tabs={TABS}
            tab={tab}
            onSelect={screen.setTab}
            label="Supplier views"
            saying={`Showing ${TABS.find((entry) => entry.key === tab)?.label ?? "Exchange"}`}
          />
        }
      />
      <div className={own.tabArea}>
        <RecordPanel tab={tab}>
          {tab === "exchange" ? (
            <div className={own.stack}>
              <SupplierExchangeOverview {...(exchange.isError ? { status: "failed" } : exchange.isPending || exchange.data === undefined
                ? { status: "pending" } : { status: "ready", rows: exchange.data })} />
              <div className={own.split}>
                <Panel
                  title="Exchanges"
                  sub={waiting === null ? "Yours to answer, and yours to chase" : waiting > 0 ? `${waiting} waiting on you` : "Nothing is waiting on you"}
                >
                  {/* **Asking** (ADR-0151). Only once the exchange has been read: who can be asked is
                      derived from it, and an unread list is not "no supplier to ask". */}
                  {exchange.data ? <RaiseRequest rows={exchange.data} /> : null}
                  <Loaded
                    query={exchange}
                    of="The supplier exchange"
                    empty={
                      <>
                        Nothing has been asked yet. A supplier request is how a figure this company does not
                        hold gets into the inventory — their fuel, their electricity, their own emissions for
                        the part of their business you buy.
                      </>
                    }
                  >
                    {(rows) => (
                      <ul className={cls(own.list)} data-testid="exchanges">
                        {rows.map((row) => (
                          <ExchangeCard
                            key={row.id}
                            row={row}
                            agreements={agreements.data ?? []}
                            // **Whether the agreement list was read at all**, which the accept panel has to
                            // know: an empty list renders as *"this supplier has not permitted a copy"* — a
                            // statement about the supplier — and a failed request produced it (EXP-29).
                            agreementsFailed={Boolean(agreements.error)}
                            agreementsPending={agreements.isPending || agreements.data === undefined}
                            onRetryAgreements={() => void agreements.refetch()}
                            mayDecide={mayDecide}
                            mayCollect={mayCollect}
                          />
                        ))}
                      </ul>
                    )}
                  </Loaded>
                </Panel>

                <PermissionsPanel
                  exchange={exchange.data}
                  agreements={agreements}
                  mayDecide={mayDecide}
                />
              </div>
            </div>
          ) : tab === "responsiveness" ? (
            <div className={own.grid12}>
              <SupplierResponsivenessPanel />
            </div>
          ) : (
            /* **Purchases are the spend-based half of Scope 3, and nothing on this part of the screen
               has a figure.** They sit on their own tab: a supplier's answer is the credible number
               and a spend line is what is left when nobody has one (ADR-0040's "D first in time" is
               the builder's order, not the reader's). */
            <div className={own.grid12}>
              <ProcurementPanels mayCollect={mayCollect} mayDecide={mayDecide} />
            </div>
          )}
        </RecordPanel>
      </div>
    </ScreenFrame>
  );
}

export function ExchangeCard({
  row,
  agreements,
  agreementsFailed,
  agreementsPending,
  onRetryAgreements,
  mayDecide,
  mayCollect,
}: {
  row: Exchange;
  agreements: readonly Agreement[];
  agreementsFailed: boolean;
  agreementsPending: boolean;
  onRetryAgreements: () => void;
  mayDecide: boolean | undefined;
  mayCollect: boolean | undefined;
}) {
  const answer = row.submissions.at(-1);
  const cue = needsYou(row, { mayCollect, mayDecide });
  return (
    <li className={cls(own.row, cue !== null && own.rowCue)} data-testid="supplier-exchange-row" data-request-id={row.id}>
      <span className={cls(own.main)}>
        <span className={cls(own.title)}>
          {row.fuel_or_carrier.replace(/_/g, " ")} · {row.category.replace(/_/g, " ")}
        </span>
        <span className={cls(own.meta)}>
          {/* **Which side the reader is on, in words.** "Buyer" alone is jargon on a screen a
              supplier also reads. */}
          {row.i_am === "buyer" ? "You asked" : row.i_am === "supplier" ? "Asked of you" : row.i_am} ·{" "}
          {shortDate(row.period_start)} – {shortDate(row.period_end)} ·{" "}
          <StatusPill tone={stateTone(row, { mayCollect, mayDecide })}>{stateLabel(row.state)}</StatusPill>
          {row.due_on ? ` · due ${shortDate(row.due_on)}` : ""}
          {cue ? <strong className={cls(own.cue)} data-testid="needs-you"> · {cue}</strong> : null}
        </span>
        {row.note ? <span className={cls(own.note)}>{row.note}</span> : null}
        {answer ? <Answer answer={answer} /> : null}
        {/* **The way into the supplier's account page** (EXP-14). Only for a row where this
            workspace is the buyer: on the supplier's side the counterparty is a customer, and the
            account page is about a supplier. */}
        {row.i_am === "buyer" ? (
          <Link
            className={styles.fixLink}
            to="/suppliers/$supplierId"
            params={{ supplierId: row.supplier_org_id }}
            search={keepSearch}
            data-testid="supplier-account-link"
          >
            Supplier account ›
          </Link>
        ) : null}
      </span>

      <span className={cls(own.side)}>
        {row.i_am === "supplier" && ["open", "answered"].includes(row.state) ? (
          mayCollect === true ? <AnswerForm row={row} /> : mayCollect === false ? (
            <p className={cls(own.unauthorized)} data-testid="supplier-answer-who-can">
              An Admin, Approver or Contributor can answer a supplier request.
            </p>
          ) : null
        ) : null}
        {row.i_am === "buyer" && answer && row.state === "answered" ? (
          mayDecide === false ? (
            <p className={cls(own.unauthorized)} data-testid="not-permitted-accept">
              <strong>An Approver accepts a supplier&rsquo;s figure.</strong> Putting another
              company&rsquo;s number in your inventory is taking responsibility for it, which is the
              same act as verifying a document.
            </p>
          ) : (
            <AcceptForm
              row={row}
              submissionId={answer.id}
              agreements={agreements}
              agreementsFailed={agreementsFailed}
              agreementsPending={agreementsPending}
              onRetryAgreements={onRetryAgreements}
            />
          )
        ) : null}
      </span>
    </li>
  );
}

function AnswerForm({ row }: { row: Exchange }) {
  const answer = useAnswerRequest(row.id);
  // **Two shapes of answer, and the supplier picks one** (S3-02). A direct figure is the better
  // answer when it exists - a supplier who metered the buyer's own delivery point has nothing to
  // divide. A share is for a supplier who only holds a total, and is where the arithmetic matters.
  const [mode, setMode] = useState<"direct" | "share">("direct");
  const [amount, setAmount] = useState("");
  const [unit, setUnit] = useState("");
  const [basis, setBasis] = useState("");
  const [shareBasis, setShareBasis] = useState<AllocationBasisName | "">("");
  const [total, setTotal] = useState("");
  const [part, setPart] = useState("");
  const [whole, setWhole] = useState("");
  const [shareNote, setShareNote] = useState("");
  const [refusal, setRefusal] = useState<unknown>(null);

  const wordsReady = unit.trim() !== "" && basis.trim().length >= 12;
  const ready =
    wordsReady &&
    (mode === "direct"
      ? amount.trim() !== ""
      : shareBasis !== "" &&
        total.trim() !== "" &&
        part.trim() !== "" &&
        whole.trim() !== "" &&
        shareNote.trim() !== "");

  function reset() {
    setAmount("");
    setUnit("");
    setBasis("");
    setShareBasis("");
    setTotal("");
    setPart("");
    setWhole("");
    setShareNote("");
    setRefusal(null);
  }

  return (
    <span className={cls(own.form)}>
      <fieldset className={cls(own.form)} data-testid="answer-mode">
        <legend>What are you sending?</legend>
        <label>
          <input type="radio" name={`mode-${row.id}`} checked={mode === "direct"} onChange={() => setMode("direct")} />{" "}
          A figure I measured for this customer
        </label>
        <label>
          <input type="radio" name={`mode-${row.id}`} checked={mode === "share"} onChange={() => setMode("share")} />{" "}
          A share of our own total
        </label>
      </fieldset>

      {mode === "direct" ? (
        <label className={styles.field}>
          <span>How much?</span>
          <input value={amount} onChange={(e) => setAmount(e.target.value)} inputMode="decimal" />
        </label>
      ) : (
        <>
          {/* **The share is not typed here, and that is the clause.** EmitIQ divides, and shows
              the buyer both numbers, so the figure is re-performable rather than asserted. There
              is deliberately no field - and no live preview - for the result: a preview computed
              in the browser would be a second implementation of the division. */}
          <p className={styles.note} data-testid="share-explainer">
            Give us your <strong>whole</strong> figure and the two numbers that split it. EmitIQ does
            the division and shows this customer all four numbers, so they can redo it - you do not
            type the share.
          </p>
          <label className={styles.field}>
            <span>Your total for the period</span>
            <input value={total} onChange={(e) => setTotal(e.target.value)} inputMode="decimal" data-testid="share-total" />
          </label>
          <label className={styles.field}>
            <span>Split by</span>
            <select value={shareBasis} onChange={(e) => setShareBasis(e.target.value as AllocationBasisName | "")} data-testid="share-basis">
              <option value="">Choose a basis…</option>
              {(Object.keys(BASIS_LABEL) as AllocationBasisName[]).map((key) => (
                <option key={key} value={key}>
                  {BASIS_LABEL[key].label}
                </option>
              ))}
            </select>
          </label>
          <label className={styles.field}>
            <span>This customer&rsquo;s part of it</span>
            <input value={part} onChange={(e) => setPart(e.target.value)} inputMode="decimal" data-testid="share-part" />
          </label>
          <label className={styles.field}>
            <span>
              The whole of it
              {shareBasis ? ` (${BASIS_LABEL[shareBasis].whole})` : ""}
            </span>
            <input value={whole} onChange={(e) => setWhole(e.target.value)} inputMode="decimal" data-testid="share-whole" />
          </label>
          <label className={styles.field}>
            {/* Whose basis and over what period. The name alone is not a documented basis - two
                suppliers both saying "by tonne-km" may have measured different things. */}
            <span>Whose basis, and over what period?</span>
            <input value={shareNote} onChange={(e) => setShareNote(e.target.value)} data-testid="share-note" />
          </label>
        </>
      )}

      <label className={styles.field}>
        <span>In what unit?</span>
        <input value={unit} onChange={(e) => setUnit(e.target.value)} placeholder="litres" />
      </label>
      <label className={styles.field}>
        {/* Required, and the button stays disabled until it is answered - the same rule a manual
            entry follows (ADR-0036). This is the only evidence the buyer will have. */}
        <span>How did you work it out?</span>
        <textarea value={basis} onChange={(e) => setBasis(e.target.value)} rows={3} />
      </label>
      <button
        type="button"
        className={styles.primary}
        data-testid="send-answer"
        disabled={!ready || answer.isPending}
        onClick={() => {
          setRefusal(null);
          const body =
            mode === "direct"
              ? { quantity: amount.trim() }
              : {
                  allocation: {
                    // `ready` guarantees a basis; the cast is the narrowing the button cannot see.
                    basis: shareBasis as AllocationBasisName,
                    supplier_total: total.trim(),
                    numerator: part.trim(),
                    denominator: whole.trim(),
                    note: shareNote.trim(),
                  },
                };
          answer
            .mutateAsync({ ...body, unit: unit.trim(), stated_basis: basis.trim() })
            .catch((failure: unknown) => setRefusal(failure));
        }}
      >
        {answer.isPending ? "Sending…" : "Send it"}
      </button>
      {/* A cancel that restores the form, once there is something in it to restore. A supplier who
          starts an answer and changes their mind should not have to clear every field by hand. */}
      {amount || unit || basis || total || part || whole || shareNote || shareBasis ? (
        <button type="button" className={styles.fixLink} onClick={reset}>
          Cancel
        </button>
      ) : null}
      {/* **No retry.** A submission that did not complete may have been recorded, and sending a
          second would be a second submission rather than the same one - the supplier would appear
          to have answered twice. The sentence sends them to look. */}
      <WriteFailed error={refusal} of="this request" />
    </span>
  );
}

function AcceptForm({
  row,
  submissionId,
  agreements,
  agreementsFailed,
  agreementsPending,
  onRetryAgreements,
}: {
  row: Exchange;
  submissionId: string;
  agreements: readonly Agreement[];
  agreementsFailed: boolean;
  agreementsPending: boolean;
  onRetryAgreements: () => void;
}) {
  const accept = useAcceptAnswer(submissionId);
  const facilitiesRead = useFacilities();
  const [site, setSite] = useState("");
  const [agreementId, setAgreementId] = useState("");
  const [refusal, setRefusal] = useState<unknown>(null);

  async function copy(postCloseReason?: string) {
    setRefusal(null);
    try {
      await accept.mutateAsync({
        facility_id: site,
        agreement_id: agreementId,
        ...(postCloseReason ? { post_close_reason: postCloseReason } : {}),
      });
    } catch (failure) {
      setRefusal(failure);
    }
  }

  /** Only the agreements **this supplier** granted **this buyer**, and only those that permit a copy
   * **for the period this answer is about** - the date the server tests (ADR-0160).
   *
   * Offering every agreement would invite the reader to pick one between two other parties, or one
   * that lapsed before the period, which the server refuses - a control that predictably fails is
   * the defect UX-04 names, and it is cheaper to not offer it than to explain it. What is *not*
   * offered is explained below, per agreement, rather than collapsed into "none".
   */
  const { usable, refused } = copyPermission(agreements, row);

  // **Before the "they have not permitted a copy" sentence**, because that sentence is a claim
  // about the supplier and a dropped request produced it. The two are opposite instructions: one
  // says go and ask them for an agreement, the other says try the read again.
  if (agreementsFailed) {
    return (
      <ReadFailed
        of="The sharing agreements"
        error={new Error("The permissions this supplier granted could not be read.")}
        onRetry={onRetryAgreements}
      />
    );
  }

  if (agreementsPending) {
    return <p className={cls(own.unauthorized)} data-testid="supplier-agreements-pending" role="status">Reading the sharing agreements…</p>;
  }

  if (usable.length === 0) {
    return (
      <div className={cls(own.unauthorized)} data-testid="no-permission">
        <p>
          <strong>This supplier has not permitted a copy.</strong> They grant that, and it is separate
          from permission to read the answer — so the figure can be read here and not filed.
        </p>
        {/* **Why, per agreement** (ADR-0160). "None" and "one that lapsed before this period" are
            different instructions: the first asks for an agreement, the second for a new one. */}
        {refused.length > 0 ? (
          <ul className={cls(own.whyNot)} data-testid="no-permission-why">
            {refused.map((entry) => (
              <li key={entry.agreement.id} data-shortfall={entry.shortfall}>
                <span>
                  {shortDate(entry.agreement.effective_from)} –{" "}
                  {entry.agreement.expires_on ? shortDate(entry.agreement.expires_on) : "no end date"}:
                </span>{" "}
                {shortfallSentence(entry, row.period_end, shortDate)}
              </li>
            ))}
          </ul>
        ) : (
          <p data-testid="no-permission-none">
            This supplier has granted this workspace no agreement at all.
          </p>
        )}
        <p>
          Ask them for an agreement that permits <em>inventory copy</em> for {shortDate(row.period_start)}{" "}
          – {shortDate(row.period_end)}.
        </p>
      </div>
    );
  }

  return (
    <span className={cls(own.form)}>
      <Loaded
        query={facilitiesRead}
        of="The list of your sites"
        empty="No sites are set up yet. Add one on Organisation before copying a figure onto it."
      >
        {(sites) => (
          <label className={styles.field}>
            <span>Which of your sites?</span>
            <select value={site} onChange={(e) => setSite(e.target.value)}>
              <option value="">Choose a site…</option>
              {sites.map((facility) => (
                <option key={facility.id} value={facility.id}>
                  {facility.name}
                </option>
              ))}
            </select>
          </label>
        )}
      </Loaded>
      <label className={styles.field}>
        <span>Under which agreement?</span>
        <select value={agreementId} onChange={(e) => setAgreementId(e.target.value)}>
          <option value="">Choose an agreement…</option>
          {usable.map((candidate) => (
            <option key={candidate.id} value={candidate.id}>
              {shortDate(candidate.effective_from)} –{" "}
              {candidate.expires_on ? shortDate(candidate.expires_on) : "no end date"}
            </option>
          ))}
        </select>
      </label>
      {/* **The grade, before the decision rather than after it.** */}
      <p className={styles.note}>
        This becomes a figure in your inventory graded <strong>C</strong> — a supplier&rsquo;s
        assertion with no document you hold. It keeps a receipt citing their submission and this
        agreement, and the report states what share of the total rests on bases like it.
      </p>
      <button
        type="button"
        className={styles.primary}
        data-testid="accept-answer"
        disabled={!site || !agreementId || accept.isPending}
        onClick={() => void copy()}
      >
        {accept.isPending ? "Copying…" : "Copy it into the inventory"}
      </button>
      {/* The answer's period is closed (ADR-0094). The supplier's answer stays unaccepted, so an
          Admin's retry is the same copy plus a reason; everyone else is told whose act it is. */}
      <PostCloseRetry
        error={refusal}
        pending={accept.isPending}
        onRecord={(reason) => void copy(reason)}
      />
      {/* A copy that may or may not have landed is the dangerous one: a second would be a second
          figure in the inventory, not the same one again. So the sentence says to look at the
          register rather than offering a button that could double it. */}
      <WriteFailed error={isPeriodClosed(refusal) ? null : refusal} of="the Emissions register" />
    </span>
  );
}

export function SuppliersScreen() {
  return (
    <RequireSession>
      <SuppliersScreenBody />
    </RequireSession>
  );
}
